// Operator dashboard — the configurable widget canvas.
//
//   DashboardView   /   widgets, their data, the layout, and the widget builder
//
// Four endpoints under /api/widgets carry the whole screen: the widget rows, one
// bulk data call keyed by widget id, the static catalogue the builder renders its
// selects from, and the layout write-back.
//
// The Vue original drove the canvas with grid-layout-plus. Here it is a plain
// 12-column CSS grid (`.wgrid`) with native HTML5 drag for reorder and a
// pointer-driven corner handle for resize — no drag library. One consequence is
// deliberate and worth knowing: a drop RE-FLOWS every cell left to right rather
// than leaving the dropped tile at an arbitrary coordinate. That is what the old
// library's `vertical-compact` did after every drag anyway, and it is the only
// cheap way to guarantee two tiles never occupy the same grid area — CSS grid
// will happily overlap explicitly-placed items, and an overlap here means one
// widget silently painted on top of another.

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type PointerEvent as ReactPointerEvent,
  type DragEvent as ReactDragEvent,
} from "react";

import { ApiError, http, qs } from "./api";
import { useSession } from "./App";
import {
  Badge,
  Banner,
  Confirm,
  Dialog,
  DialogFooter,
  Empty,
  Field,
  Menu,
  Num,
  RelativeTime,
  SkeletonRows,
  Spinner,
  Table,
  Toggle,
  useAsync,
  useToast,
  type BadgeTone,
  type Column,
} from "./ui";
import { BarChartCard, DonutCard, LineChartCard, type BarDatum, type ChartSeries } from "./charts";
import { BLUE_RAMP, SERIES, readMode, type Mode } from "./theme";

// ============================================================ payloads

export interface WidgetFilter {
  field: string;
  operator: string;
  value: string;
}

export interface DashboardWidget {
  id: string;
  name: string;
  description: string;
  data_source: string;
  metric: string;
  field: string;
  filters: WidgetFilter[];
  display_type: string;
  chart_type: string;
  group_by_field: string;
  show_change: boolean;
  color: string;
  size: string;
  display_order: number;
  grid_x: number;
  grid_y: number;
  grid_w: number;
  grid_h: number;
  config: Record<string, unknown>;
  is_shared: boolean;
  is_default: boolean;
  is_owner: boolean;
  created_by: string;
  created_at: string;
  updated_at: string;
}

interface ChartPoint {
  label: string;
  value: number;
}

interface DataPoint {
  label: string;
  value: number;
  color?: string | null;
}

interface GroupedSeriesDataset {
  label: string;
  data: number[];
}

interface GroupedSeries {
  labels: string[];
  datasets: GroupedSeriesDataset[];
}

interface WidgetTableRow {
  id: string;
  label: string;
  sub_label: string;
  status: string;
  direction: string;
  created_at: string;
}

/**
 * The four collection fields arrive as `null` — not `[]` — when the widget's
 * display type does not produce them. The server documents that as load-bearing,
 * so every read here goes through a null check rather than a `.length`.
 */
interface WidgetData {
  widget_id?: string;
  value: number;
  change: number;
  prev_value: number;
  chart_data: ChartPoint[] | null;
  data_points: DataPoint[] | null;
  grouped_series: GroupedSeries | null;
  table_rows: WidgetTableRow[] | null;
}

interface WidgetListResponse {
  widgets: DashboardWidget[];
}

interface WidgetDataResponse {
  data: Record<string, WidgetData>;
}

interface DataSourceInfo {
  name: string;
  label: string;
  fields: string[];
  metrics: string[];
  display_types: string[];
}

interface OperatorInfo {
  value: string;
  label: string;
}

interface CatalogueResponse {
  data_sources: DataSourceInfo[];
  metrics: string[];
  display_types: string[];
  operators: OperatorInfo[];
}

interface LayoutItem {
  id: string;
  grid_x: number;
  grid_y: number;
  grid_w: number;
  grid_h: number;
}

interface WidgetPayload {
  name: string;
  description: string;
  data_source: string;
  metric: string;
  field: string;
  filters: WidgetFilter[];
  display_type: string;
  chart_type: string;
  group_by_field: string;
  show_change: boolean;
  color: string;
  size: string;
  config: Record<string, unknown>;
  is_shared: boolean;
}

// ============================================================ small helpers

function errMessage(err: unknown, fallback: string): string {
  if (err instanceof ApiError) return err.message || fallback;
  if (err instanceof Error && err.message) return err.message;
  return fallback;
}

function titleCase(value: string): string {
  return String(value || "")
    .replace(/_/g, " ")
    .replace(/^\w/, (c) => c.toUpperCase());
}

/** The Vue formatter, kept exactly: 1.2M / 3.4K / 57. */
function compactNumber(n: number): string {
  if (!Number.isFinite(n)) return "—";
  const abs = Math.abs(n);
  if (abs >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (abs >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return String(Math.round(n));
}

function widgetValueText(widget: DashboardWidget, value: number): string {
  return widget.display_type === "percentage" ? `${value.toFixed(1)}%` : compactNumber(value);
}

function initials(label: string): string {
  const letters = label
    .split(" ")
    .filter(Boolean)
    .map((part) => part[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
  return letters || "?";
}

function shortcutKeys(widget: DashboardWidget): string[] {
  const raw = widget.config?.shortcuts;
  return Array.isArray(raw) ? raw.filter((k): k is string => typeof k === "string") : [];
}

/** Delivery state is a status, so it takes a status tone and carries its own word. */
function statusTone(status: string): BadgeTone {
  if (status === "read") return "good";
  if (status === "failed") return "critical";
  if (status === "delivered") return "info";
  return "neutral";
}

/**
 * The console's theme lives on a `data-theme` attribute with no React context
 * behind it, so anything that reads a palette colour has to watch the attribute
 * itself or it stays painted in the mode it first mounted in.
 */
function useThemeMode(): Mode {
  const [mode, setMode] = useState<Mode>(readMode);
  useEffect(() => {
    const sync = () => setMode(readMode());
    const obs = new MutationObserver(sync);
    obs.observe(document.documentElement, { attributes: true, attributeFilter: ["data-theme"] });
    return () => obs.disconnect();
  }, []);
  return mode;
}

// ============================================================ presentation maps

const DATA_SOURCE_GLYPH: Record<string, string> = {
  messages: "✉",
  contacts: "◍",
  sessions: "✦",
  campaigns: "➤",
  transfers: "⇄",
  chatbot_summary: "◔",
  nps: "★",
};

const glyphFor = (source: string) => DATA_SOURCE_GLYPH[source] ?? "▦";

const WIDGET_COLORS = ["blue", "green", "purple", "orange", "red", "cyan"] as const;

const COLOR_LABEL: Record<string, string> = {
  blue: "Blue",
  green: "Green",
  purple: "Purple",
  orange: "Orange",
  red: "Red",
  cyan: "Cyan",
};

/**
 * The widget colour is the operator's decoration for the tile's glyph, nothing
 * more. It is resolved through the shared palette rather than a private hex list
 * so it stays legible on both surfaces — and it deliberately does NOT colour the
 * chart series, which the shared chart cards own.
 */
function chipColor(name: string, mode: Mode): string {
  switch (name) {
    case "green":
      return SERIES[mode][2];
    case "purple":
      return SERIES[mode][6];
    case "orange":
      return SERIES[mode][1];
    case "red":
      return SERIES[mode][7];
    case "cyan":
      return BLUE_RAMP[2];
    default:
      return SERIES[mode][0];
  }
}

const METRIC_LABEL: Record<string, string> = {
  count: "Count",
  sum: "Sum",
  avg: "Average",
  total_conversations: "Total conversations",
  completion_rate: "Completion rate",
  unique_users: "Unique users",
  avg_conversations_per_user: "Avg conversations per user",
  avg_resolution_seconds: "Avg resolution (seconds)",
  nps_score: "NPS score",
  total_responses: "Total responses",
  promoters: "Promoters",
  passives: "Passives",
  detractors: "Detractors",
};

const metricLabel = (metric: string) => METRIC_LABEL[metric] ?? titleCase(metric);

const DISPLAY_TYPE_LABEL: Record<string, string> = {
  number: "Number",
  percentage: "Percentage",
  chart: "Chart",
  table: "Table",
  shortcuts: "Shortcuts",
};

const displayTypeLabel = (value: string) => DISPLAY_TYPE_LABEL[value] ?? titleCase(value);

interface ShortcutSpec {
  label: string;
  to: string;
  icon: string;
}

/** Mirrors the shell's nav so a shortcut never points at a screen that moved.
 *
 * That promise is only kept if this map is edited whenever a route is. `chat`,
 * `transfers` and `cannedResponses` were left behind when the live-agent inbox came out
 * of scope, so a saved shortcuts widget sent the operator to the Not-found page. A dead
 * shortcut is a small thing that reads as a broken product, so the entries go with the
 * routes rather than being kept "just in case".
 */
const SHORTCUTS: Record<string, ShortcutSpec> = {
  documents: { label: "Documents", to: "/documents", icon: "▥" },
  templates: { label: "Templates", to: "/templates", icon: "▣" },
  chatbot: { label: "Chatbot", to: "/chatbot", icon: "✦" },
  contacts: { label: "Contacts", to: "/settings/contacts", icon: "☏" },
  settings: { label: "Settings", to: "/settings", icon: "⚙" },
  accounts: { label: "WhatsApp accounts", to: "/settings/accounts", icon: "▤" },
  tags: { label: "Tags", to: "/settings/tags", icon: "◈" },
  teams: { label: "Teams", to: "/settings/teams", icon: "◎" },
  users: { label: "Users", to: "/settings/users", icon: "◍" },
  roles: { label: "Roles", to: "/settings/roles", icon: "◇" },
};

const SHORTCUT_ORDER = Object.keys(SHORTCUTS);

/** Sources whose numbers are analytics aggregations: no filters, no grouping. */
const COMPOSITE_SOURCES = new Set(["chatbot_summary", "nps"]);

// ============================================================ date range

type RangePreset = "today" | "7days" | "30days" | "this_month" | "custom";

interface DateRange {
  from: string;
  to: string;
}

const RANGE_KEY = "dashboard_time_range";
const CUSTOM_KEY = "dashboard_custom_range";

const RANGE_LABEL: Record<RangePreset, string> = {
  today: "Today",
  "7days": "Last 7 days",
  "30days": "Last 30 days",
  this_month: "This month",
  custom: "Custom range",
};

const COMPARISON_LABEL: Record<RangePreset, string> = {
  today: "vs yesterday",
  "7days": "vs previous 7 days",
  "30days": "vs previous 30 days",
  this_month: "vs last month",
  custom: "vs previous period",
};

/** Local calendar date, never UTC — an ISO string would shift the day boundary. */
function localDate(d: Date): string {
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${month}-${day}`;
}

function resolveRange(preset: RangePreset, custom: DateRange): DateRange {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
  const back = (days: number) => new Date(now.getFullYear(), now.getMonth(), now.getDate() - days);

  switch (preset) {
    case "today":
      return { from: localDate(today), to: localDate(today) };
    case "7days":
      return { from: localDate(back(7)), to: localDate(today) };
    case "30days":
      return { from: localDate(back(30)), to: localDate(today) };
    case "custom":
      if (custom.from && custom.to) return { from: custom.from, to: custom.to };
      return { from: localDate(monthStart), to: localDate(today) };
    default:
      return { from: localDate(monthStart), to: localDate(today) };
  }
}

interface CalendarParts {
  year: number;
  month: number;
  day: number;
}

// The saved shape is the Vue console's ({start,end} calendar parts) so the two
// builds can be run side by side against one browser profile without either of
// them tripping over the other's preferences.
function readSavedCustom(): DateRange {
  try {
    const raw = localStorage.getItem(CUSTOM_KEY);
    if (!raw) return { from: "", to: "" };
    const parsed = JSON.parse(raw) as { start?: CalendarParts; end?: CalendarParts };
    if (!parsed.start || !parsed.end) return { from: "", to: "" };
    const iso = (p: CalendarParts) =>
      `${p.year}-${String(p.month).padStart(2, "0")}-${String(p.day).padStart(2, "0")}`;
    return { from: iso(parsed.start), to: iso(parsed.end) };
  } catch {
    return { from: "", to: "" };
  }
}

function writeSavedCustom(range: DateRange): void {
  const parts = (value: string): CalendarParts | null => {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
    return m ? { year: Number(m[1]), month: Number(m[2]), day: Number(m[3]) } : null;
  };
  const start = parts(range.from);
  const end = parts(range.to);
  if (!start || !end) return;
  localStorage.setItem(CUSTOM_KEY, JSON.stringify({ start, end }));
}

function readSavedPreset(): RangePreset {
  const saved = localStorage.getItem(RANGE_KEY);
  return saved === "today" || saved === "7days" || saved === "30days" || saved === "custom"
    ? saved
    : "this_month";
}

// ============================================================ grid geometry

const GRID_COLS = 12;
const ROW_H = 40;
/** Matches `.wgrid { gap: var(--sp-4) }`. */
const GAP = 16;
const MIN_W = 2;
const MIN_H = 2;
const MAX_H = 30;

interface Cell {
  id: string;
  x: number;
  y: number;
  w: number;
  h: number;
}

const clamp = (v: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, v));

function defaultSize(displayType: string): { w: number; h: number } {
  if (displayType === "chart") return { w: 6, h: 5 };
  if (displayType === "table" || displayType === "shortcuts") return { w: 6, h: 8 };
  return { w: 3, h: 3 };
}

/**
 * Stored placement wins; a widget that has never been positioned (`grid_w === 0`
 * — the shape rows seeded before the grid existed have it) is flowed in after
 * everything that has, small tiles first.
 */
function computeLayout(widgets: DashboardWidget[]): Cell[] {
  const positioned = widgets.filter((w) => w.grid_w > 0);
  const legacy = widgets.filter((w) => w.grid_w === 0);

  const cells: Cell[] = positioned.map((w) => ({
    id: w.id,
    x: w.grid_x,
    y: w.grid_y,
    w: w.grid_w,
    h: w.grid_h,
  }));

  if (legacy.length === 0) return cells;

  const large = new Set(["chart", "table", "shortcuts"]);
  const ordered = [
    ...legacy.filter((w) => !large.has(w.display_type)),
    ...legacy.filter((w) => large.has(w.display_type)),
  ];

  let y = positioned.length > 0 ? Math.max(...positioned.map((w) => w.grid_y + w.grid_h)) : 0;
  let x = 0;
  let rowH = 0;
  for (const w of ordered) {
    const size = defaultSize(w.display_type);
    if (x + size.w > GRID_COLS) {
      x = 0;
      y += rowH;
      rowH = 0;
    }
    cells.push({ id: w.id, x, y, w: size.w, h: size.h });
    x += size.w;
    rowH = Math.max(rowH, size.h);
  }
  return cells;
}

const visualOrder = (cells: Cell[]) => [...cells].sort((a, b) => a.y - b.y || a.x - b.x);

/** Left-to-right packing. See the file header for why a drop re-flows everything. */
function flow(order: Cell[]): Cell[] {
  const out: Cell[] = [];
  let x = 0;
  let y = 0;
  let rowH = 0;
  for (const cell of order) {
    const w = clamp(cell.w, MIN_W, GRID_COLS);
    const h = clamp(cell.h, MIN_H, MAX_H);
    if (x + w > GRID_COLS) {
      x = 0;
      y += rowH;
      rowH = 0;
    }
    out.push({ id: cell.id, x, y, w, h });
    x += w;
    rowH = Math.max(rowH, h);
  }
  return out;
}

function reorder(cells: Cell[], dragId: string, overId: string): Cell[] {
  const order = visualOrder(cells);
  const from = order.findIndex((c) => c.id === dragId);
  const to = order.findIndex((c) => c.id === overId);
  if (from < 0 || to < 0 || from === to) return cells;
  const [moved] = order.splice(from, 1);
  order.splice(to, 0, moved);
  return flow(order);
}

// ============================================================ export button

function WidgetExport({ widget, range }: { widget: DashboardWidget; range: DateRange }) {
  const toast = useToast();
  const [busy, setBusy] = useState(false);

  const run = async (format: "csv" | "json") => {
    if (busy) return;
    setBusy(true);
    try {
      const res = await http.raw(
        `/widgets/${widget.id}/export${qs({ from: range.from, to: range.to, format })}`
      );
      const url = URL.createObjectURL(await res.blob());
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = `${widget.name.replace(/[^\w-]+/g, "_") || "widget"}_export.${format}`;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(url);
      toast.push(`${widget.name} exported`, "good");
    } catch (err) {
      toast.push(errMessage(err, "Could not export this widget"), "critical");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Menu
      items={[
        { label: "Export CSV", disabled: busy, onSelect: () => void run("csv") },
        { label: "Export JSON", disabled: busy, onSelect: () => void run("json") },
      ]}
      trigger={
        <button className="btn icon sm ghost" title="Export" aria-label={`Export ${widget.name}`}>
          {busy ? <Spinner label="Exporting" /> : "⭳"}
        </button>
      }
    />
  );
}

// ============================================================ widget body

function ChartBody({ widget, data }: { widget: DashboardWidget; data: WidgetData }) {
  const chartData = data.chart_data ?? [];
  const points = data.data_points ?? [];
  const grouped = data.grouped_series;
  const type = widget.chart_type || "line";

  const empty =
    chartData.length === 0 && points.length === 0 && (grouped?.datasets.length ?? 0) === 0;
  if (empty) return <div className="widget-void">No data for this period</div>;

  if (type === "pie") {
    const source: BarDatum[] = (points.length > 0 ? points : chartData).map((d) => ({
      label: d.label,
      value: d.value,
    }));
    return <DonutCard title="" data={source} />;
  }

  if (type === "bar") {
    const source: BarDatum[] = (points.length > 0 ? points : chartData).map((d) => ({
      label: d.label,
      value: d.value,
    }));
    return <BarChartCard title="" data={source} />;
  }

  // Line: a grouped widget answers with one dataset per group, an ungrouped one
  // with a single time series.
  const series: ChartSeries[] =
    grouped && grouped.datasets.length > 0
      ? grouped.datasets.map((ds) => ({
          name: ds.label,
          points: grouped.labels.map((label, i) => ({ x: label, y: ds.data[i] ?? null })),
        }))
      : [{ name: widget.name, points: chartData.map((d) => ({ x: d.label, y: d.value })) }];

  return <LineChartCard title="" series={series} />;
}

const GROUPED_COLUMNS = (groupField: string): Column<DataPoint>[] => [
  { key: "label", header: groupField.toUpperCase() || "GROUP", sortable: true },
  {
    key: "value",
    header: "Count",
    align: "right",
    sortable: true,
    render: (row) => <Num value={row.value} decimals={0} />,
  },
];

function TableBody({ widget, data }: { widget: DashboardWidget; data: WidgetData }) {
  const points = data.data_points ?? [];
  const rows = data.table_rows ?? [];

  if (widget.group_by_field && points.length > 0) {
    return <Table columns={GROUPED_COLUMNS(widget.group_by_field)} rows={points} />;
  }

  if (rows.length > 0) {
    return (
      <div className="col">
        {rows.map((row) => (
          <div className="row" key={row.id} style={{ alignItems: "flex-start" }}>
            <span
              className="widget-chip"
              style={{ background: "var(--surface-3)", color: "var(--text-2)", fontSize: 11 }}
              aria-hidden
            >
              {initials(row.label)}
            </span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div className="row">
                <span className="strong truncate">{row.label}</span>
                <span className="spacer" />
                <span className="muted" style={{ fontSize: 11, flex: "none" }}>
                  <RelativeTime value={row.created_at} />
                </span>
              </div>
              {row.sub_label && (
                <div className="muted truncate" style={{ fontSize: 12 }}>
                  {row.sub_label}
                </div>
              )}
              {(row.direction || row.status) && (
                <div className="chips" style={{ marginTop: 4 }}>
                  {/* Direction is a channel fact, not a health state, so it never
                      borrows a status tone. */}
                  {row.direction && (
                    <Badge tone={row.direction === "incoming" ? "channel" : "info"}>
                      {row.direction}
                    </Badge>
                  )}
                  {row.status && <Badge tone={statusTone(row.status)}>{row.status}</Badge>}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    );
  }

  return <div className="widget-void">No data for this period</div>;
}

function ShortcutsBody({ widget }: { widget: DashboardWidget }) {
  const keys = shortcutKeys(widget).filter((k) => SHORTCUTS[k]);
  if (keys.length === 0) return <div className="widget-void">No shortcuts chosen yet</div>;
  return (
    <div className="shortcuts">
      {keys.map((key) => {
        const spec = SHORTCUTS[key];
        return (
          // draggable=false: an anchor drags itself by default, which would steal
          // the drop from the widget cell in layout-edit mode.
          <a className="shortcut" key={key} href={spec.to} draggable={false}>
            <span className="glyph" aria-hidden>
              {spec.icon}
            </span>
            {spec.label}
          </a>
        );
      })}
    </div>
  );
}

function NumberBody({
  widget,
  data,
  comparison,
}: {
  widget: DashboardWidget;
  data: WidgetData | undefined;
  comparison: string;
}) {
  const value = data?.value ?? 0;
  const change = data?.change ?? 0;
  const tone = change > 0 ? "up" : change < 0 ? "down" : "flat";
  const glyph = change > 0 ? "▲" : change < 0 ? "▼" : "→";

  return (
    <div className="stat">
      <div className="value">{widgetValueText(widget, value)}</div>
      {widget.show_change && data && (
        <div className={`delta ${tone}`}>
          <span aria-hidden>{glyph}</span>
          <span>{Math.abs(change).toFixed(1)}%</span>
          <span className="muted">{comparison}</span>
        </div>
      )}
    </div>
  );
}

// ============================================================ widget cell

interface WidgetCellProps {
  widget: DashboardWidget;
  cell: Cell;
  data: WidgetData | undefined;
  dataLoading: boolean;
  editing: boolean;
  dragging: boolean;
  over: boolean;
  range: DateRange;
  comparison: string;
  canEdit: boolean;
  canDelete: boolean;
  canExport: boolean;
  onEdit: (widget: DashboardWidget) => void;
  onDelete: (widget: DashboardWidget) => void;
  onDragStart: (id: string) => void;
  onDragOver: (id: string) => void;
  onDrop: (id: string) => void;
  onDragEnd: () => void;
  onResizeStart: (id: string, e: ReactPointerEvent<HTMLElement>) => void;
  onResizeMove: (e: ReactPointerEvent<HTMLElement>) => void;
  onResizeEnd: () => void;
}

function WidgetCell(props: WidgetCellProps) {
  const {
    widget,
    cell,
    data,
    dataLoading,
    editing,
    dragging,
    over,
    range,
    comparison,
    canEdit,
    canDelete,
    canExport,
  } = props;
  const mode = useThemeMode();

  const kind = widget.display_type;
  const isChart = kind === "chart";
  const isTable = kind === "table";
  const isShortcuts = kind === "shortcuts";
  const isNumber = !isChart && !isTable && !isShortcuts;

  // The server refuses both of these, so the control is not offered.
  const exportable =
    canExport &&
    !isShortcuts &&
    !(isTable && widget.data_source === "messages" && !widget.group_by_field);

  const style = {
    "--gx": cell.x + 1,
    "--gy": cell.y + 1,
    "--gw": cell.w,
    "--gh": cell.h,
  } as CSSProperties;

  const classes = ["widget", "card"];
  if (dragging) classes.push("dragging");
  if (over) classes.push("over");

  const handleDragOver = (e: ReactDragEvent<HTMLDivElement>) => {
    if (!editing) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    props.onDragOver(widget.id);
  };

  return (
    <div
      className={classes.join(" ")}
      style={style}
      draggable={editing}
      onDragStart={(e) => {
        e.dataTransfer.effectAllowed = "move";
        // Firefox will not start a drag without payload on the transfer.
        e.dataTransfer.setData("text/plain", widget.id);
        props.onDragStart(widget.id);
      }}
      onDragOver={handleDragOver}
      onDragEnter={handleDragOver}
      onDrop={(e) => {
        e.preventDefault();
        props.onDrop(widget.id);
      }}
      onDragEnd={props.onDragEnd}
    >
      {editing && (
        <span className="widget-grip" aria-hidden>
          ⠿
        </span>
      )}

      {!editing && (canEdit || canDelete || exportable) && (
        <div className="widget-actions row">
          {exportable && <WidgetExport widget={widget} range={range} />}
          {canEdit && (
            <button
              className="btn icon sm ghost"
              title="Edit widget"
              aria-label={`Edit ${widget.name}`}
              onClick={() => props.onEdit(widget)}
            >
              ✎
            </button>
          )}
          {canDelete && (
            <button
              className="btn icon sm ghost"
              title="Delete widget"
              aria-label={`Delete ${widget.name}`}
              onClick={() => props.onDelete(widget)}
            >
              ✕
            </button>
          )}
        </div>
      )}

      <div className="row" style={{ alignItems: "flex-start", marginBottom: "var(--sp-2)" }}>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div className="label truncate" style={{ fontSize: 12, color: "var(--text-3)", fontWeight: 500 }}>
            {widget.name}
          </div>
          {widget.description && <p className="widget-note truncate">{widget.description}</p>}
        </div>
        {/* The row-list and shortcut tiles carry no glyph: their rows already say
            what they are, and the header would only repeat it. */}
        {!isShortcuts && !isTable && (
          <span
            className="widget-chip"
            style={{ background: "var(--surface-3)", color: chipColor(widget.color, mode) }}
            aria-hidden
          >
            {glyphFor(widget.data_source)}
          </span>
        )}
      </div>

      {dataLoading && !isShortcuts ? (
        <div className="widget-body">
          <SkeletonRows n={isNumber ? 1 : 3} cols={2} />
        </div>
      ) : isNumber ? (
        <NumberBody widget={widget} data={data} comparison={comparison} />
      ) : isShortcuts ? (
        <div className="widget-body">
          <ShortcutsBody widget={widget} />
        </div>
      ) : isTable ? (
        <div className="widget-body">
          {data ? <TableBody widget={widget} data={data} /> : <div className="widget-void">No data</div>}
        </div>
      ) : data ? (
        <ChartBody widget={widget} data={data} />
      ) : (
        <div className="widget-void">No data</div>
      )}

      {editing && (
        <button
          className="widget-resize"
          aria-label={`Resize ${widget.name}`}
          onPointerDown={(e) => props.onResizeStart(widget.id, e)}
          onPointerMove={props.onResizeMove}
          onPointerUp={props.onResizeEnd}
          onPointerCancel={props.onResizeEnd}
        />
      )}
    </div>
  );
}

// ============================================================ builder dialog

interface WidgetForm {
  name: string;
  description: string;
  data_source: string;
  metric: string;
  field: string;
  filters: WidgetFilter[];
  display_type: string;
  chart_type: string;
  group_by_field: string;
  show_change: boolean;
  color: string;
  size: string;
  config: Record<string, unknown>;
  is_shared: boolean;
  shortcuts: string[];
}

function emptyForm(): WidgetForm {
  return {
    name: "",
    description: "",
    data_source: "",
    metric: "count",
    field: "",
    filters: [],
    display_type: "number",
    chart_type: "",
    group_by_field: "",
    show_change: true,
    color: "blue",
    size: "small",
    config: {},
    is_shared: false,
    shortcuts: [],
  };
}

function formFromWidget(widget: DashboardWidget): WidgetForm {
  return {
    name: widget.name,
    description: widget.description,
    data_source: widget.data_source,
    metric: widget.metric,
    field: widget.field,
    filters: widget.filters.map((f) => ({ ...f })),
    display_type: widget.display_type,
    chart_type: widget.chart_type,
    group_by_field: widget.group_by_field || "",
    show_change: widget.show_change,
    color: widget.color || "blue",
    size: widget.size,
    config: widget.config ?? {},
    is_shared: widget.is_shared,
    shortcuts: widget.display_type === "shortcuts" ? shortcutKeys(widget) : [],
  };
}

interface BuilderProps {
  open: boolean;
  widget: DashboardWidget | null;
  catalogue: CatalogueResponse | null;
  onClose: () => void;
  onSaved: () => void;
}

function WidgetBuilder({ open, widget, catalogue, onClose, onSaved }: BuilderProps) {
  const toast = useToast();
  const [form, setForm] = useState<WidgetForm>(emptyForm);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  useEffect(() => {
    if (!open) return;
    setForm(widget ? formFromWidget(widget) : emptyForm());
    setError(null);
  }, [open, widget]);

  const sources = catalogue?.data_sources ?? [];
  const operators = catalogue?.operators ?? [];
  const source = sources.find((s) => s.name === form.data_source);
  const isShortcuts = form.display_type === "shortcuts";
  const isComposite = COMPOSITE_SOURCES.has(form.data_source);

  const availableFields = source?.fields ?? [];
  const availableMetrics = source?.metrics?.length ? source.metrics : ["count", "sum", "avg"];

  const availableDisplayTypes = useMemo(() => {
    const base = isShortcuts
      ? ["number", "percentage", "chart", "table", "shortcuts"]
      : source?.display_types?.length
        ? source.display_types
        : ["number", "percentage", "chart", "table"];
    // "shortcuts" is not a rendering OF a source, so it is offered alongside the
    // source's own list — except for the composite sources, which have no tile.
    return isComposite || base.includes("shortcuts") ? base : [...base, "shortcuts"];
  }, [isShortcuts, isComposite, source]);

  // The Vue watchers, as explicit transitions. Written here rather than in an
  // effect so opening the dialog on an existing widget cannot rewrite its own
  // saved combination on the way in.
  const changeDataSource = (name: string) => {
    setForm((f) => {
      const next = sources.find((s) => s.name === name);
      const metrics = next?.metrics?.length ? next.metrics : ["count", "sum", "avg"];
      const types = next?.display_types ?? [];
      return {
        ...f,
        data_source: name,
        metric: metrics.includes(f.metric) ? f.metric : metrics[0],
        display_type: types.length && !types.includes(f.display_type) ? types[0] : f.display_type,
        // A filter or grouping is only meaningful against the source it was
        // picked from, so switching source clears them rather than carrying an
        // unresolvable field across.
        filters: [],
        group_by_field: "",
        chart_type: "",
      };
    });
  };

  const changeMetric = (metric: string) => {
    setForm((f) => ({
      ...f,
      metric,
      display_type: metric === "completion_rate" ? "percentage" : f.display_type,
    }));
  };

  const changeDisplayType = (display_type: string) => {
    setForm((f) => ({
      ...f,
      display_type,
      chart_type: display_type === "chart" ? f.chart_type || "line" : "",
      group_by_field:
        display_type === "chart" || display_type === "table" ? f.group_by_field : "",
      data_source: display_type === "shortcuts" ? "" : f.data_source,
      metric: display_type === "shortcuts" ? "count" : f.metric,
    }));
  };

  const setFilter = (index: number, patch: Partial<WidgetFilter>) => {
    setForm((f) => ({
      ...f,
      filters: f.filters.map((row, i) => (i === index ? { ...row, ...patch } : row)),
    }));
  };

  const save = async () => {
    if (!form.name.trim()) {
      toast.push("A widget needs a name", "warning");
      return;
    }
    if (!isShortcuts && !form.data_source) {
      toast.push("Pick a data source", "warning");
      return;
    }

    const payload: WidgetPayload = {
      name: form.name.trim(),
      description: form.description,
      data_source: form.data_source,
      metric: form.metric,
      field: form.field,
      // A half-typed filter row would be rejected by the server, so it is dropped
      // rather than sent.
      filters: form.filters.filter((f) => f.field && f.operator && f.value),
      display_type: form.display_type,
      chart_type: form.chart_type,
      group_by_field: form.group_by_field,
      show_change: form.show_change,
      color: form.color,
      size: form.size,
      config: isShortcuts ? { shortcuts: [...form.shortcuts] } : form.config,
      is_shared: form.is_shared,
    };

    setSaving(true);
    setError(null);
    try {
      if (widget) await http.put<DashboardWidget>(`/widgets/${widget.id}`, payload);
      else await http.post<DashboardWidget>("/widgets", payload);
      toast.push(widget ? "Widget updated" : "Widget created", "good");
      onSaved();
      onClose();
    } catch (err) {
      setError(err instanceof ApiError ? err : new ApiError(0, "client_error", errMessage(err, "Save failed"), ""));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} title={widget ? "Edit widget" : "Create widget"}>
      <p className="muted" style={{ marginBottom: "var(--sp-4)" }}>
        Choose what the tile counts and how it should be drawn.
      </p>

      {error && (
        <Banner tone="critical" traceId={error.traceId}>
          Could not save the widget: {error.message}
        </Banner>
      )}

      <Field label="Name" required>
        <input
          value={form.name}
          placeholder="Messages this month"
          onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
        />
      </Field>

      <Field label="Description">
        <textarea
          rows={2}
          value={form.description}
          placeholder="What this tile answers"
          onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
        />
      </Field>

      {!isShortcuts && (
        <Field label="Data source" required>
          <select value={form.data_source} onChange={(e) => changeDataSource(e.target.value)}>
            <option value="">Select a data source…</option>
            {sources.map((s) => (
              <option key={s.name} value={s.name}>
                {s.label}
              </option>
            ))}
          </select>
        </Field>
      )}

      {!isShortcuts && form.display_type !== "table" && (
        <Field label="Metric">
          <select value={form.metric} onChange={(e) => changeMetric(e.target.value)}>
            {availableMetrics.map((m) => (
              <option key={m} value={m}>
                {metricLabel(m)}
              </option>
            ))}
          </select>
        </Field>
      )}

      <Field label="Display type">
        <select value={form.display_type} onChange={(e) => changeDisplayType(e.target.value)}>
          {availableDisplayTypes.map((t) => (
            <option key={t} value={t}>
              {displayTypeLabel(t)}
            </option>
          ))}
        </select>
      </Field>

      {form.display_type === "chart" && (
        <Field label="Chart type">
          <select
            value={form.chart_type}
            onChange={(e) => setForm((f) => ({ ...f, chart_type: e.target.value }))}
          >
            <option value="line">Line</option>
            <option value="bar">Bar</option>
            <option value="pie">Pie</option>
          </select>
        </Field>
      )}

      {!isComposite &&
        (form.display_type === "chart" || form.display_type === "table") &&
        form.data_source && (
          <Field label="Group by" hint="Leave on “None” for a time series.">
            <select
              value={form.group_by_field || "none"}
              onChange={(e) =>
                setForm((f) => ({
                  ...f,
                  group_by_field: e.target.value === "none" ? "" : e.target.value,
                }))
              }
            >
              <option value="none">None (time series)</option>
              {availableFields.map((field) => (
                <option key={field} value={field}>
                  {field}
                </option>
              ))}
            </select>
          </Field>
        )}

      {isShortcuts && (
        <div className="field">
          <label>Shortcuts</label>
          <div style={{ maxHeight: 240, overflowY: "auto" }}>
            {SHORTCUT_ORDER.map((key) => {
              const spec = SHORTCUTS[key];
              const checked = form.shortcuts.includes(key);
              return (
                <label className="check" key={key} style={{ padding: "4px 0" }}>
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={(e) =>
                      setForm((f) => ({
                        ...f,
                        shortcuts: e.target.checked
                          ? [...f.shortcuts, key]
                          : f.shortcuts.filter((k) => k !== key),
                      }))
                    }
                  />
                  <span aria-hidden>{spec.icon}</span>
                  <span>{spec.label}</span>
                </label>
              );
            })}
          </div>
        </div>
      )}

      {!isShortcuts && !isComposite && (
        <div className="field">
          <div className="row">
            <label style={{ flex: 1 }}>Filters ({form.filters.length})</label>
            <button
              type="button"
              className="btn sm"
              onClick={() =>
                setForm((f) => ({
                  ...f,
                  filters: [...f.filters, { field: "", operator: "equals", value: "" }],
                }))
              }
            >
              + Add filter
            </button>
          </div>
          {!form.data_source && form.filters.length === 0 && (
            <span className="hint">Pick a data source first.</span>
          )}
          {form.filters.map((filter, index) => (
            <div className="filter-row" key={index}>
              <select
                value={filter.field}
                aria-label={`Filter ${index + 1} field`}
                onChange={(e) => setFilter(index, { field: e.target.value })}
              >
                <option value="">Field…</option>
                {availableFields.map((field) => (
                  <option key={field} value={field}>
                    {field}
                  </option>
                ))}
              </select>
              <select
                className="op"
                value={filter.operator}
                aria-label={`Filter ${index + 1} operator`}
                onChange={(e) => setFilter(index, { operator: e.target.value })}
              >
                {operators.map((op) => (
                  <option key={op.value} value={op.value}>
                    {op.label}
                  </option>
                ))}
              </select>
              <input
                value={filter.value}
                placeholder="Value"
                aria-label={`Filter ${index + 1} value`}
                onChange={(e) => setFilter(index, { value: e.target.value })}
              />
              <button
                type="button"
                className="btn icon sm ghost"
                aria-label={`Remove filter ${index + 1}`}
                onClick={() =>
                  setForm((f) => ({ ...f, filters: f.filters.filter((_, i) => i !== index) }))
                }
              >
                ✕
              </button>
            </div>
          ))}
        </div>
      )}

      {!isShortcuts && form.display_type !== "table" && (
        <Field label="Colour" hint="Tints the tile's glyph only.">
          <select
            value={form.color}
            onChange={(e) => setForm((f) => ({ ...f, color: e.target.value }))}
          >
            {WIDGET_COLORS.map((c) => (
              <option key={c} value={c}>
                {COLOR_LABEL[c]}
              </option>
            ))}
          </select>
        </Field>
      )}

      <div className="col">
        {(form.display_type === "number" || form.display_type === "percentage") && (
          <Toggle
            checked={form.show_change}
            onChange={(show_change) => setForm((f) => ({ ...f, show_change }))}
            label="Show the change against the previous period"
          />
        )}
        <Toggle
          checked={form.is_shared}
          onChange={(is_shared) => setForm((f) => ({ ...f, is_shared }))}
          label="Share with the team"
        />
      </div>

      <DialogFooter>
        <button className="btn" onClick={onClose} disabled={saving}>
          Cancel
        </button>
        <button className="btn primary" onClick={() => void save()} disabled={saving}>
          {saving && <Spinner label="Saving" />}
          {widget ? "Update" : "Create"}
        </button>
      </DialogFooter>
    </Dialog>
  );
}

// ============================================================ the screen

export function DashboardView() {
  const { me, can } = useSession();
  const toast = useToast();

  const canWrite = can("analytics", "write");
  const canDeleteAny = can("analytics", "delete");
  // The shell's `can` only knows read/write/delete, but the export routes require
  // their own action, so it is read straight off the session.
  const canExport = Boolean(me?.is_super_admin || me?.permissions?.analytics?.includes("export"));

  // ---- range -------------------------------------------------------------
  const [preset, setPreset] = useState<RangePreset>(readSavedPreset);
  const [customApplied, setCustomApplied] = useState<DateRange>(readSavedCustom);
  const [customDraft, setCustomDraft] = useState<DateRange>(readSavedCustom);

  const range = useMemo(() => resolveRange(preset, customApplied), [preset, customApplied]);
  const comparison = COMPARISON_LABEL[preset];

  const changePreset = (next: RangePreset) => {
    setPreset(next);
    localStorage.setItem(RANGE_KEY, next);
  };

  const applyCustom = () => {
    if (!customDraft.from || !customDraft.to) return;
    setCustomApplied(customDraft);
    writeSavedCustom(customDraft);
  };

  // ---- data --------------------------------------------------------------
  const [version, setVersion] = useState(0);
  const reloadAll = useCallback(() => setVersion((v) => v + 1), []);

  const boot = useAsync(async () => {
    const [list, catalogue] = await Promise.all([
      http.get<WidgetListResponse>("/widgets"),
      http.get<CatalogueResponse>("/widgets/data-sources"),
    ]);
    return { widgets: list.widgets ?? [], catalogue };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [version]);

  const dataQuery = useAsync(async () => {
    const res = await http.get<WidgetDataResponse>(
      `/widgets/data${qs({ from: range.from, to: range.to })}`
    );
    return res.data ?? {};
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range.from, range.to, version]);

  const widgets = useMemo(() => boot.data?.widgets ?? [], [boot.data]);
  const catalogue = boot.data?.catalogue ?? null;
  const widgetData = dataQuery.data ?? {};

  // ---- layout ------------------------------------------------------------
  const [cells, setCells] = useState<Cell[]>([]);
  const cellsRef = useRef<Cell[]>([]);
  cellsRef.current = cells;

  useEffect(() => {
    setCells(computeLayout(widgets));
  }, [widgets]);

  const [editing, setEditingState] = useState(false);
  const [dragId, setDragId] = useState<string | null>(null);
  const [overId, setOverId] = useState<string | null>(null);
  const gridRef = useRef<HTMLDivElement>(null);
  const saveTimer = useRef<number | null>(null);

  const persist = useCallback(
    async (list: Cell[]) => {
      if (list.length === 0) return;
      const layout: LayoutItem[] = list.map((c) => ({
        id: c.id,
        grid_x: c.x,
        grid_y: c.y,
        grid_w: c.w,
        grid_h: c.h,
      }));
      try {
        await http.post<{ message?: string }>("/widgets/layout", { layout });
      } catch (err) {
        toast.push(errMessage(err, "Could not save the layout"), "critical");
      }
    },
    [toast]
  );

  const queueSave = useCallback(() => {
    if (saveTimer.current) window.clearTimeout(saveTimer.current);
    saveTimer.current = window.setTimeout(() => {
      saveTimer.current = null;
      void persist(cellsRef.current);
    }, 500);
  }, [persist]);

  useEffect(
    () => () => {
      if (saveTimer.current) window.clearTimeout(saveTimer.current);
    },
    []
  );

  const setEditing = (on: boolean) => {
    if (!on) {
      // Leaving edit mode flushes: an operator who clicks Done and navigates away
      // in under half a second must not lose the arrangement.
      if (saveTimer.current) {
        window.clearTimeout(saveTimer.current);
        saveTimer.current = null;
      }
      void persist(cellsRef.current);
    }
    setEditingState(on);
    setDragId(null);
    setOverId(null);
  };

  const handleDrop = (targetId: string) => {
    if (!dragId || dragId === targetId) {
      setDragId(null);
      setOverId(null);
      return;
    }
    setCells((cs) => reorder(cs, dragId, targetId));
    setDragId(null);
    setOverId(null);
    queueSave();
  };

  // ---- resize ------------------------------------------------------------
  const resizeRef = useRef<{ id: string; x: number; y: number; w: number; h: number } | null>(null);

  const onResizeStart = (id: string, e: ReactPointerEvent<HTMLElement>) => {
    const cell = cellsRef.current.find((c) => c.id === id);
    if (!cell) return;
    e.preventDefault();
    e.stopPropagation();
    resizeRef.current = { id, x: e.clientX, y: e.clientY, w: cell.w, h: cell.h };
    e.currentTarget.setPointerCapture(e.pointerId);
  };

  const onResizeMove = (e: ReactPointerEvent<HTMLElement>) => {
    const start = resizeRef.current;
    const gridWidth = gridRef.current?.clientWidth ?? 0;
    if (!start || gridWidth <= 0) return;

    const cellWidth = (gridWidth - GAP * (GRID_COLS - 1)) / GRID_COLS;
    const w = clamp(start.w + Math.round((e.clientX - start.x) / (cellWidth + GAP)), MIN_W, GRID_COLS);
    const h = clamp(start.h + Math.round((e.clientY - start.y) / (ROW_H + GAP)), MIN_H, MAX_H);

    setCells((cs) => {
      const current = cs.find((c) => c.id === start.id);
      if (!current || (current.w === w && current.h === h)) return cs;
      // Re-flow after the size change for the same reason a drop re-flows: a
      // widened tile would otherwise land on top of its neighbour.
      return flow(visualOrder(cs).map((c) => (c.id === start.id ? { ...c, w, h } : c)));
    });
  };

  const onResizeEnd = () => {
    if (!resizeRef.current) return;
    resizeRef.current = null;
    queueSave();
  };

  // ---- widget CRUD -------------------------------------------------------
  const [builderOpen, setBuilderOpen] = useState(false);
  const [editingWidget, setEditingWidget] = useState<DashboardWidget | null>(null);
  const [pendingDelete, setPendingDelete] = useState<DashboardWidget | null>(null);

  const openCreate = () => {
    setEditingWidget(null);
    setBuilderOpen(true);
  };

  const openEdit = (widget: DashboardWidget) => {
    setEditingWidget(widget);
    setBuilderOpen(true);
  };

  const confirmDelete = async () => {
    if (!pendingDelete) return;
    try {
      await http.del<{ message?: string }>(`/widgets/${pendingDelete.id}`);
      toast.push("Widget deleted", "good");
      setPendingDelete(null);
      reloadAll();
    } catch (err) {
      toast.push(errMessage(err, "Could not delete the widget"), "critical");
      throw err;
    }
  };

  // ---- render ------------------------------------------------------------
  const showEditLayout = canWrite && widgets.length > 1;

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <h1>Dashboard</h1>
          <div className="sub">Your widgets, over the period you choose.</div>
        </div>
        <div className="page-actions">
          {canWrite && (
            <button className="btn primary" onClick={openCreate}>
              + Add widget
            </button>
          )}
          {showEditLayout && (
            <button
              className={`btn${editing ? " primary" : ""}`}
              onClick={() => setEditing(!editing)}
            >
              {editing ? "Done" : "⠿ Edit layout"}
            </button>
          )}
          <select
            value={preset}
            aria-label="Date range"
            style={{ width: 180 }}
            onChange={(e) => changePreset(e.target.value as RangePreset)}
          >
            {(Object.keys(RANGE_LABEL) as RangePreset[]).map((key) => (
              <option key={key} value={key}>
                {RANGE_LABEL[key]}
              </option>
            ))}
          </select>
          {preset === "custom" && (
            <>
              <input
                type="date"
                value={customDraft.from}
                aria-label="Range start"
                onChange={(e) => setCustomDraft((r) => ({ ...r, from: e.target.value }))}
              />
              <input
                type="date"
                value={customDraft.to}
                aria-label="Range end"
                onChange={(e) => setCustomDraft((r) => ({ ...r, to: e.target.value }))}
              />
              <button
                className="btn"
                disabled={!customDraft.from || !customDraft.to}
                onClick={applyCustom}
              >
                Apply
              </button>
            </>
          )}
        </div>
      </div>

      {boot.error && (
        <Banner tone="critical" traceId={boot.error.traceId}>
          Could not load your widgets: {boot.error.message}
        </Banner>
      )}
      {!boot.error && dataQuery.error && (
        <Banner tone="critical" traceId={dataQuery.error.traceId}>
          Could not load the widget numbers: {dataQuery.error.message}
        </Banner>
      )}
      {editing && (
        <Banner tone="info">
          Drag a tile onto another to reorder, or drag the corner handle to resize. Tiles re-flow
          left to right so none of them overlap.
        </Banner>
      )}

      {boot.loading ? (
        <div className="grid c4">
          {[0, 1, 2, 3].map((i) => (
            <div className="card" key={i}>
              <SkeletonRows n={2} cols={2} />
            </div>
          ))}
        </div>
      ) : widgets.length === 0 ? (
        <Empty
          title="No widgets yet"
          body="Build a tile for the number you check first thing in the morning — a count, a trend, a breakdown, or a row of shortcuts."
          icon="▦"
          action={
            canWrite ? (
              <button className="btn primary" onClick={openCreate}>
                + Add widget
              </button>
            ) : undefined
          }
        />
      ) : (
        <div ref={gridRef} className={`wgrid${editing ? " editing" : ""}`}>
          {cells.map((cell) => {
            const widget = widgets.find((w) => w.id === cell.id);
            if (!widget) return null;
            return (
              <WidgetCell
                key={cell.id}
                widget={widget}
                cell={cell}
                data={widgetData[cell.id]}
                dataLoading={dataQuery.loading}
                editing={editing}
                dragging={dragId === cell.id}
                over={overId === cell.id && dragId !== null && dragId !== cell.id}
                range={range}
                comparison={comparison}
                // Only the owner may write or delete a widget; the seeded shared
                // tiles belong to the bootstrap admin, so offering the control to
                // anyone else would guarantee a 403.
                canEdit={canWrite && widget.is_owner}
                canDelete={canDeleteAny && widget.is_owner}
                canExport={canExport}
                onEdit={openEdit}
                onDelete={setPendingDelete}
                onDragStart={setDragId}
                onDragOver={setOverId}
                onDrop={handleDrop}
                onDragEnd={() => {
                  setDragId(null);
                  setOverId(null);
                }}
                onResizeStart={onResizeStart}
                onResizeMove={onResizeMove}
                onResizeEnd={onResizeEnd}
              />
            );
          })}
        </div>
      )}

      <WidgetBuilder
        open={builderOpen}
        widget={editingWidget}
        catalogue={catalogue}
        onClose={() => setBuilderOpen(false)}
        onSaved={reloadAll}
      />

      <Confirm
        open={pendingDelete !== null}
        onClose={() => setPendingDelete(null)}
        onConfirm={confirmDelete}
        danger
        title="Delete this widget?"
        body={`"${pendingDelete?.name ?? ""}" will be removed from your dashboard. This cannot be undone.`}
        confirmLabel="Delete widget"
      />
    </div>
  );
}
